import { Injectable, OnDestroy, effect, inject, signal, untracked } from '@angular/core';
import { Subscription, TimeoutError, timeout } from 'rxjs';
import { AssistantSupportService } from './assistant-support.service';
import { ASSISTANT_CONFIG } from './assistant.config';
import { ASSISTANT_CONTEXT, assistantContextKey } from './assistant-context';
import { AssistantMessage } from './assistant.models';
import { AssistantVoiceService } from './assistant-voice.service';
import {
  HubCentralChatApiService,
  AssistantUnavailableError,
  AssistantApiError,
} from './hub-central-chat-api.service';

/** Owns one in-memory conversation. No transcript or token is persisted in browser storage. */
@Injectable({ providedIn: 'root' })
export class AssistantConversationService implements OnDestroy {
  private readonly voice = inject(AssistantVoiceService);
  readonly config = inject(ASSISTANT_CONFIG);
  private readonly support = inject(AssistantSupportService);
  private readonly chatApi = inject(HubCentralChatApiService);
  private readonly readContext = inject(ASSISTANT_CONTEXT);
  private contextKey: string | null = null;
  private readonly messageState = signal<readonly AssistantMessage[]>([]);
  private readonly pendingState = signal(false);
  readonly messages = this.messageState.asReadonly();
  readonly pending = this.pendingState.asReadonly();
  readonly draft = signal('');
  private sessionId: string | null = null;
  private generation = 0;
  private nextId = 0;
  private request?: Subscription;

  constructor() {
    effect(() => {
      const key = assistantContextKey(this.readContext());
      if (this.contextKey !== null && key !== this.contextKey) untracked(() => this.reset());
    });
  }

  send(): boolean {
    if (this.support.phase() !== 'idle') return false;
    const question = this.draft().trim();
    if (!question || question.length > this.config.maxQuestionLength || this.pending())
      return false;
    const context = this.readContext();
    const contextKey = assistantContextKey(context);
    // A different partner/role context must not inherit the previous conversation session.
    if (this.contextKey !== null && this.contextKey !== contextKey) this.reset();
    this.contextKey = contextKey;
    // Assign before dispatch so queue retries and failed requests retain the conversation ID.
    this.sessionId ??= crypto.randomUUID();
    const generation = this.generation;
    const userId = ++this.nextId;
    const id = ++this.nextId;
    const answerId = crypto.randomUUID();
    this.messageState.update((messages) => [
      ...messages,
      { id: userId, role: 'user', text: question, status: 'complete', createdAt: new Date().toISOString() },
      { id, role: 'assistant', text: '', status: 'pending', createdAt: new Date().toISOString() },
    ]);
    this.draft.set('');
    this.pendingState.set(true);
    // Install the holder first: the API service can complete synchronously.
    const request = new Subscription();
    this.request = request;
    let completed = false;
    let pendingHandoff: string | null = null;
    const fail = (error: unknown) => {
      if (generation !== this.generation || completed) return;
      completed = true;
      const text =
        error instanceof AssistantUnavailableError
          ? 'Your question was not sent. The Hub Central knowledge service is not connected yet.'
          : error instanceof AssistantApiError
            ? {
                timeout: 'The response timed out. Please try again.',
                busy: 'Hub Assistant is busy. Please try again shortly.',
                unavailable: 'Hub Assistant is temporarily unavailable. Please try again later.',
                retrieval: 'I couldn’t retrieve the guidance right now. Please try your question again.',
                invalid: 'Your message could not be processed. Please check it and try again.',
                session:
                  'Your conversation could not be loaded or saved. Please try again or start a new chat.',
              }[error.reason]
            : error instanceof TimeoutError
              ? 'The response timed out. Please try again.'
              : 'The response could not be completed. Please try again.';
      this.updateMessage(id, text, 'error');
      this.voice.cancel();
      this.support.handoffFailed();
      this.pendingState.set(false);
      request.unsubscribe();
    };
    try {
      request.add(
        this.chatApi
          .send({ question, sessionId: this.sessionId, requestId: answerId, context })
          .pipe(timeout({ each: this.config.responseTimeoutMs }))
          .subscribe({
            next: (event) => {
              // A reset invalidates callbacks even if the API service delivers a late response.
              if (generation !== this.generation || completed) return;
              // Check synchronously: a response can arrive before Angular runs context effects.
              if (contextKey !== assistantContextKey(this.readContext())) {
                this.reset();
                return;
              }
              if (event.type === 'support_pending') {
                pendingHandoff = event.handoffId;
                this.support.prepare(event.sessionId, event.handoffId);
              } else if (event.type === 'support_completed' || event.type === 'feedback_completed') {
                fail(new Error('Unexpected support command response'));
              } else if (event.type === 'chunk') {
                this.messageState.update((messages) =>
                  messages.map((message) =>
                    message.id === id ? { ...message, text: message.text + event.text } : message,
                  ),
                );
              } else {
                completed = true;
                this.sessionId = event.sessionId;
                // Completion replaces partial text, including a late safe refusal.
                this.messageState.update((messages) =>
                  messages.map((message) =>
                    message.id === id
                      ? {
                          ...message,
                          text: event.answer,
                          status: 'complete',
                          responseStatus: event.status,
                          incomplete: event.incomplete,
                          answerId: event.feedbackAvailable ? answerId : undefined,
                          citations: event.citations,
                          navigationIds: event.navigationIds,
                        }
                      : message,
                  ),
                );
                if (event.feedbackAvailable) void this.loadFeedback(id);
                this.pendingState.set(false);
                request.unsubscribe();
                if (event.handoff === 'live_agent') {
                  this.voice.cancel();
                  if (this.support.enabled) void this.support.completeHandoff(event.sessionId,
                    event.support, pendingHandoff);
                  else this.updateMessage(id,
                    'Live agent chat is not available in this environment. Please use the approved Hub Support contact.',
                    'complete');
                } else this.voice.answerCompleted(event.answer);
              }
            },
            error: fail,
            complete: () => {
              if (!completed) fail(new Error('Incomplete response'));
            },
          }),
      );
    } catch (error) {
      fail(error);
    }
    return true;
  }

  setFeedbackDraft(id: AssistantMessage['id'], value: string): void {
    this.patchFeedback(id, { feedbackDraft: value.slice(0, 1000), feedbackConfirmation: undefined });
  }

  async saveFeedbackComment(id: AssistantMessage['id']): Promise<void> {
    const message = this.messages().find(item => item.id === id);
    if (message?.feedback?.vote !== 'down' ||
        (message.feedbackDraft ?? '') === message.feedback.comment) return;
    await this.feedbackOperation(id, 'down', true);
  }

  async loadFeedback(id: AssistantMessage['id']): Promise<void> {
    await this.feedbackOperation(id);
  }

  async saveFeedback(id: AssistantMessage['id'], vote: 'up' | 'down' | null): Promise<void> {
    await this.feedbackOperation(id, vote);
  }

  private async feedbackOperation(id: AssistantMessage['id'], vote?: 'up' | 'down' | null, saveComment = false): Promise<void> {
    const message = this.messages().find(item => item.id === id);
    const session = this.sessionId;
    const context = this.readContext();
    const key = assistantContextKey(context);
    if (!message?.answerId || !session || message.feedbackBusy ||
        this.contextKey !== key || (vote !== undefined && (!message.feedback || message.feedbackError))) return;
    const generation = this.generation;
    this.patchFeedback(id, { feedbackBusy: true, feedbackError: false, feedbackConfirmation: undefined });
    try {
      const feedback = await this.chatApi.feedback(session, context, {
        action: vote === undefined ? 'getFeedback' : 'saveFeedback', answerId: message.answerId,
        vote, comment: vote === 'down'
          ? (saveComment ? message.feedbackDraft ?? '' : message.feedback?.comment ?? '') : '',
        revision: message.feedback?.revision ?? 0,
      });
      if (generation !== this.generation || key !== assistantContextKey(this.readContext())) return;
      this.patchFeedback(id, { feedback, feedbackBusy: false, feedbackError: false,
        feedbackDraft: feedback.comment,
        feedbackConfirmation: saveComment ? 'comment-saved' : undefined });
    } catch {
      if (generation !== this.generation || key !== assistantContextKey(this.readContext())) return;
      this.patchFeedback(id, { feedbackBusy: false, feedbackError: true });
    }
  }

  private patchFeedback(id: AssistantMessage['id'], patch: Partial<AssistantMessage>): void {
    this.messageState.update(messages => messages.map(message => message.id === id ? { ...message, ...patch } : message));
  }

  startSupport(): void {
    if (this.pending() || this.support.active()) return;
    const context = this.readContext();
    const key = assistantContextKey(context);
    if (this.contextKey !== null && key !== this.contextKey) this.reset();
    this.contextKey = key;
    this.sessionId ??= crypto.randomUUID();
    void this.support.start(this.sessionId);
  }

  /** New chat and logout invalidate the old request before clearing its state. */
  reset(): void {
    this.support.reset();
    this.voice.cancel();
    this.generation++;
    this.request?.unsubscribe();
    this.request = undefined;
    this.sessionId = null;
    this.contextKey = null;
    this.messageState.set([]);
    this.draft.set('');
    this.pendingState.set(false);
  }

  ngOnDestroy(): void {
    this.reset();
  }

  private updateMessage(id: number, text: string, status: AssistantMessage['status']): void {
    this.messageState.update((messages) =>
      messages.map((message) => (message.id === id ? { ...message, text, status } : message)),
    );
  }
}
